import { inject, Injectable } from '@angular/core';
import {
  AccountService,
  BaseRefreshDataService,
  BatchImportStoreService,
  DecaCompanyStoreService,
  DecaStoreService,
  UsersStoreService,
} from '@okcargo-apps/shared-application-ionic';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { type InfiniteScrollRefreshSource } from '@okcargo-apps/layout-ionic';

@Injectable({
  providedIn: 'root',
})
export class RefreshDataService extends BaseRefreshDataService {
  private readonly accountService = inject(AccountService);
  private readonly usersStoreService = inject(UsersStoreService);

  private readonly decaStoreService = inject(DecaStoreService);
  private readonly decaCompanyStoreService = inject(DecaCompanyStoreService);
  private readonly batchImportStoreService = inject(BatchImportStoreService);

  protected override performRefreshAll(): void {
    this.refreshAllDocuments();
    this.refreshAvailableCredits();
    this.refreshCompanies();
    this.refreshBatchImports();

    // Users are NOT refreshed here (polling / general pull-to-refresh): the query loads on
    // application start (declaratively, keeping data for offline mode) and reloads when the
    // users list component initializes. See `reloadUsersOnComponentInit`.
  }

  public refreshAllFromUser(): void {
    this.refreshAllDocumentsFromUser();
    this.refreshAvailableCredits();
    this.refreshCompanies();
    this.refreshBatchImports();
  }

  public refreshAllDocuments(): void {
    this.refreshDocumentsAsShipper();
    this.refreshDocumentsAsCarrier();
  }

  public refreshAllDocumentsFromUser(): void {
    this.refreshDocumentsAsShipperFromUser();
    this.refreshDocumentsAsCarrierFromUser();
  }

  public refreshDocumentsAsShipper(): void {
    if (this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER)) {
      this.decaStoreService.reloadDocumentsAsShipper('polling');
    }
  }

  public refreshDocumentsAsShipperFromUser(): void {
    if (this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER)) {
      this.decaStoreService.reloadDocumentsAsShipper('user');
    }
  }

  public refreshDocumentsAsCarrier(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_DRIVER)
    ) {
      this.decaStoreService.reloadDocumentsAsCarrier('polling');
    }
  }

  public refreshDocumentsAsCarrierFromUser(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_DRIVER)
    ) {
      this.decaStoreService.reloadDocumentsAsCarrier('user');
    }
  }

  public refreshCurrentDocument(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_DRIVER)
    ) {
      this.decaStoreService.reloadCurrentDocument();
    }
  }

  public refreshAvailableCredits(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER)
    ) {
      this.decaStoreService.reloadAvailableCredits();
    }
  }

  public refreshCompanies(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER)
    ) {
      this.decaCompanyStoreService.reloadCompanies();
    }
  }

  public refreshBatchImports(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER)
    ) {
      this.batchImportStoreService.reloadBatchImports();
    }
  }

  public refreshCurrentBatchImport(): void {
    if (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER)
    ) {
      this.batchImportStoreService.reloadCurrentBatchImport();
    }
  }

  /** Page-scoped refresh: reloads only the users list. */
  public refreshUsers(source: InfiniteScrollRefreshSource = 'user'): void {
    this.usersStoreService.reloadUsers(source);
  }
}
